import { useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Download, FileBarChart2 } from "lucide-react";

import { ApiError, formatCLP } from "../../lib/api";
import { useToast } from "../../components/Toast";
import {
  Badge,
  Button,
  Card,
  Cargando,
  ErrorCarga,
  PageHeader,
  TableWrap,
  type BadgeTone,
} from "../../components/ui";
import { MESES } from "../cierres/estado";
import {
  descargarRendicionCsv,
  fetchRendicion,
  listarOrganismos,
  type EstadoAplicacion,
} from "./api";

const HOY = new Date();

export function RendicionPage(): JSX.Element {
  const toast = useToast();

  const [mes, setMes] = useState<number>(HOY.getMonth() + 1);
  const [anio, setAnio] = useState<number>(HOY.getFullYear());
  const [organismoId, setOrganismoId] = useState<string>(""); // "" = todos

  const organismosQ = useQuery({
    queryKey: ["organismos", false],
    queryFn: () => listarOrganismos(false),
  });

  const rendicionQ = useQuery({
    queryKey: ["rendicion", mes, anio, organismoId],
    queryFn: () =>
      fetchRendicion(mes, anio, organismoId ? Number(organismoId) : null),
  });

  const descargaMut = useMutation({
    mutationFn: () =>
      descargarRendicionCsv(mes, anio, organismoId ? Number(organismoId) : null),
    onError: (err) => {
      const msg =
        err instanceof ApiError ? err.message : "No se pudo descargar el CSV";
      toast.error(msg);
    },
  });

  const totales = rendicionQ.data?.totales;
  const lineas = rendicionQ.data?.lineas ?? [];

  const aniosOptions = useMemo(() => {
    const base = HOY.getFullYear();
    return [base + 1, base, base - 1, base - 2];
  }, []);

  return (
    <div className="space-y-4">
      <PageHeader
        title="Rendición FNDR"
        icon={FileBarChart2}
        subtitle="Agrega los subsidios aplicados del periodo. Filtra por organismo para emitir la rendición específica al ente subsidiador. El CSV es genérico y se versionará cuando el formato exacto SISS/FNDR esté validado."
        actions={
          <Button
            icon={Download}
            onClick={() => descargaMut.mutate()}
            disabled={
              descargaMut.isPending ||
              !rendicionQ.data ||
              (totales?.cantidad_aplicaciones ?? 0) === 0
            }
          >
            {descargaMut.isPending ? "Descargando…" : "Descargar CSV"}
          </Button>
        }
      />

      {/* Filtros */}
      <Card padding="sm">
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <label className="inline-flex items-center gap-2">
            <span className="text-xs text-neutral-600">Mes</span>
            <select
              value={mes}
              onChange={(e) => setMes(Number(e.target.value))}
              className="rounded-md border border-neutral-450 px-3 py-1.5 text-sm shadow-sm"
            >
              {MESES.map((nombre, idx) => (
                <option key={idx} value={idx + 1}>
                  {nombre}
                </option>
              ))}
            </select>
          </label>
          <label className="inline-flex items-center gap-2">
            <span className="text-xs text-neutral-600">Año</span>
            <select
              value={anio}
              onChange={(e) => setAnio(Number(e.target.value))}
              className="rounded-md border border-neutral-450 px-3 py-1.5 text-sm shadow-sm"
            >
              {aniosOptions.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
          </label>
          <label className="inline-flex items-center gap-2">
            <span className="text-xs text-neutral-600">Organismo</span>
            <select
              value={organismoId}
              onChange={(e) => setOrganismoId(e.target.value)}
              className="rounded-md border border-neutral-450 px-3 py-1.5 text-sm shadow-sm"
            >
              <option value="">— Todos —</option>
              {organismosQ.data?.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.codigo} — {o.nombre}
                </option>
              ))}
            </select>
          </label>
        </div>
      </Card>

      {rendicionQ.isPending && <Cargando texto="Cargando rendición…" />}
      {rendicionQ.isError && (
        <ErrorCarga
          error={rendicionQ.error}
          mensaje="Error al cargar la rendición."
          onReintentar={() => void rendicionQ.refetch()}
        />
      )}

      {totales && (
        <>
          {/* Totales */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Tile
              label="Aplicaciones"
              value={String(totales.cantidad_aplicaciones)}
              detail={`${totales.cantidad_aplicados} aplicados · ${totales.cantidad_cobrados} cobrados · ${totales.cantidad_rechazados} rechazados`}
            />
            <Tile
              label="m³ subsidiados"
              value={totales.total_m3_subsidiados.toLocaleString("es-CL")}
            />
            <Tile
              label="Monto aplicado"
              value={formatCLP(totales.total_monto_aplicado)}
            />
            <Tile
              label="Recuperado FNDR"
              value={formatCLP(totales.total_monto_recuperado_fndr)}
              detail={`Pendiente: ${formatCLP(totales.total_monto_pendiente)}`}
            />
          </div>

          {totales.organismo_codigo && (
            <p className="text-xs text-neutral-500">
              Organismo: <strong>{totales.organismo_codigo}</strong> —{" "}
              {totales.organismo_nombre}
            </p>
          )}
        </>
      )}

      {/* Tabla de líneas */}
      {lineas.length === 0 && !rendicionQ.isPending && !rendicionQ.isError && (
        <p className="text-sm text-neutral-500">
          Sin aplicaciones de subsidio en el periodo seleccionado.
        </p>
      )}

      {lineas.length > 0 && (
        <TableWrap label="Rendición del periodo">
          <table className="min-w-full divide-y divide-neutral-200 text-sm">
            <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th scope="col" className="px-3 py-2 text-left">Socio</th>
                <th scope="col" className="px-3 py-2 text-left">Sector / Arranque</th>
                <th scope="col" className="px-3 py-2 text-left">Decreto</th>
                <th scope="col" className="px-3 py-2 text-right">m³ subs</th>
                <th scope="col" className="px-3 py-2 text-right">Aplicado</th>
                <th scope="col" className="px-3 py-2 text-right">Recuperado</th>
                <th scope="col" className="px-3 py-2 text-center">Estado</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {lineas.map((l) => (
                <tr key={l.aplicado_id} className="hover:bg-neutral-50">
                  <td className="px-3 py-2">
                    <div className="font-medium text-neutral-900">
                      {l.socio_nombre}
                    </div>
                    <div className="text-xs text-neutral-500">{l.socio_rut}</div>
                  </td>
                  <td className="px-3 py-2 text-neutral-600">
                    <div>{l.sector_nombre ?? "—"}</div>
                    <div className="text-xs text-neutral-500">
                      {l.arranque_codigo ?? "—"}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-neutral-600">
                    <div className="font-mono text-xs">{l.decreto}</div>
                    <div className="text-xs text-neutral-500">
                      {l.porcentaje_subsidio}% · tope {l.tope_m3} m³
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {l.m3_subsidiados}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {formatCLP(l.monto_aplicado)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {formatCLP(l.monto_recuperado_fndr)}
                  </td>
                  <td className="px-3 py-2 text-center">
                    <EstadoBadge estado={l.estado} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
      )}
    </div>
  );
}

function Tile({
  label,
  value,
  detail,
}: {
  label: string;
  value: string;
  detail?: string;
}): JSX.Element {
  return (
    <Card padding="sm">
      <div className="text-xs uppercase tracking-wider text-neutral-500">
        {label}
      </div>
      <div className="mt-1 text-lg font-semibold text-neutral-900 tabular-nums">
        {value}
      </div>
      {detail && (
        <div className="mt-1 text-xs text-neutral-500">{detail}</div>
      )}
    </Card>
  );
}

function EstadoBadge({ estado }: { estado: EstadoAplicacion }): JSX.Element {
  const tones: Record<EstadoAplicacion, BadgeTone> = {
    Aplicado: "amber",
    Cobrado_FNDR: "emerald",
    Rechazado_FNDR: "rose",
  };
  const label =
    estado === "Cobrado_FNDR"
      ? "Cobrado"
      : estado === "Rechazado_FNDR"
        ? "Rechazado"
        : estado;
  return <Badge tone={tones[estado]}>{label}</Badge>;
}
